import React, { createContext, useEffect, useState, useContext, useMemo } from "react";
import { db } from "../firebase";
import { format } from "date-fns";
import { ProfileContext } from "./ProfileContext";
import { query, where, onSnapshot, collectionGroup, orderBy, collection } from "firebase/firestore";
import { WORKFLOW_WERKER_TYPES, FB_COLLECTION_PATH } from "../functions/postActionTypes";

export const WerkerJobContext = createContext<any>({});

// -------------------- Helpers --------------------
const FIVE_MIN_GRACE_MS = 5 * 60 * 1000;

/**
 * Best-effort conversion to millis.
 * Supports:
 * - number (epoch ms)
 * - string (ISO date)
 * - Date
 * - Firestore Timestamp-like objects { toMillis(): number }
 */
function toMillis(v: any): number | null {
  if (v == null) return null;

  if (typeof v === "number" && Number.isFinite(v)) return v;

  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isFinite(t) ? t : null;
  }

  // Firestore Timestamp-like
  if (typeof v === "object" && typeof v.toMillis === "function") {
    const t = v.toMillis();
    return typeof t === "number" && Number.isFinite(t) ? t : null;
  }

  if (typeof v === "string") {
    const t = new Date(v).getTime();
    return Number.isFinite(t) ? t : null;
  }

  return null;
}

/**
 * Omit "available" jobs that are effectively in the past,
 * using FINISH time (not start time) with a 5-minute grace period.
 *
 * Keep:
 * - cancelled/expired (so your UI can ghost them if desired)
 * - available jobs with missing/invalid finish time (fail open)
 */
function shouldKeepFeedRow(r: any, nowMs: number): boolean {
  // dismissed handled separately; this is just time/status logic
  if (r?.status !== "available") return true;

  const finishMs = toMillis(r?.finishDateTimeRaw);
  if (finishMs == null) return true; // fail open if bad data

  // Keep if finish time is still ahead of (now - grace)
  return finishMs > nowMs - FIVE_MIN_GRACE_MS;
}

export const WerkerJobContextProvider: React.FC<any> = ({ children }) => {
  const { profile } = useContext(ProfileContext);

  const [availableJobsCount, setAvailableJobsCount] = useState(0);
  const [scheduledJobsCount, setScheduledJobsCount] = useState(0);
  const [pastJobsCount, setPastJobsCount] = useState(0);

  const [badgeCountInvitedSegmentWerkerContext, setBadgeCountInvitedSegmentWerkerContext] = React.useState(0);
  const [badgeCountScheduledSegmentWerkerContext, setBadgeCountScheduledSegmentWerkerContext] = React.useState(0);
  const [badgeCountPastSegmentWerkerContext, setBadgeCountPastSegmentWerkerContext] = React.useState(0);

  // -------------------- DEBUG: which listener fired --------------------
  const [debugFeedSnapCount, setDebugFeedSnapCount] = useState(0);
  const [debugLegacySnapCount, setDebugLegacySnapCount] = useState(0);

  interface WerkerJobData {
    JobDocId: string;
    MyJobDocId: string;
    ShiftIdentifier: string;
    SchedUid: string;
    DateDay: string;
    EndDay: string;
    StartDateTime: string;
    FinishDateTime: string;
    StartDateTimeRaw: string;
    FinishDateTimeRaw: string;
    Company: string;
    Location: string;
    LocationCoordinates: string;
    Pay: string;
    ShiftNotes: string;

    // legacy-only fields used by Scheduled/Past list
    NumJobSlots: number;
    JobStatusSched: string;
    JobStatusWerker: string;
    IsPaidJob: boolean;
    PaidWerker: boolean;
    JobCancelledByScheduler: boolean;
    CreatedAt: number;
    UpdatedAt: number;
    IsRead: boolean;
    IsNewMessage: boolean;
    SchedFirstName: string;
    SchedLastName: string;
    SchedProfilePic: string;
    ScheduledCount: number;
  }

  const [werkerJob, setWerkerJob] = useState<WerkerJobData[]>([]);

  // --- Feed docs: users/{uid}/jobFeed/{jobId} ---
  // Single list source
  type FeedDoc = {
    jobId: string;
    werkerJobDocId?: string;
    schedUid: string;
    status: "available" | "expired" | "cancelled";

    company: string;
    location: string;
    locationCoordinates: string;
    pay: string;
    shiftIdentifier: string;
    shiftNotes: string;
    startDateTimeRaw: any; // allow Timestamp/number/string defensively
    finishDateTimeRaw: any; // allow Timestamp/number/string defensively
    schedFirstName: string;
    schedLastName: string;
    schedProfilePicUrl: string;

    createdAt: number;
    updatedAt?: number;

    isRead?: boolean;
    isNewMessage?: boolean;
    dismissed?: boolean;
    dismissedAt?: number | null;
  };

  const [invitedFeedRaw, setInvitedFeedRaw] = useState<FeedDoc[]>([]);

  useEffect(() => {
    const uid = profile?.UserId;
    if (!uid) return;

    const feedQ = query(
      collection(db, FB_COLLECTION_PATH.Users, uid, FB_COLLECTION_PATH.JobFeed),
      where("status", "in", ["available", "expired", "cancelled"]),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      feedQ,
      (snap) => {
        setDebugFeedSnapCount((p) => p + 1);

        const rows: FeedDoc[] = [];
        snap.forEach((d) => rows.push(d.data() as FeedDoc));

        // 1) remove dismissed
        const notDismissed = rows.filter((r) => r?.dismissed !== true);

        // 2) remove "available" jobs that are now in the past (using FINISH time + 5 min grace)
        const nowMs = Date.now();
        const eligible = notDismissed.filter((r) => shouldKeepFeedRow(r, nowMs));

        setInvitedFeedRaw(eligible);

        // counts + badges should be computed from the SAME filtered list
        // IMPORTANT: invited badge totals should ONLY include eligible "available" jobs (exclude expired/cancelled ghosts)
        const available = eligible.filter((r) => r.status === "available");
        setAvailableJobsCount(available.length);

        // Badge rule per job:
        // +1 if unread (isRead === false)
        // +1 if has new messages (isNewMessage === true)
        // So a job can contribute 0, 1, or 2.
        const unreadJobs = available.reduce((acc, r) => acc + (r?.isRead === false ? 1 : 0), 0);
        const newMsgJobs = available.reduce((acc, r) => acc + (r?.isNewMessage === true ? 1 : 0), 0);

        setBadgeCountInvitedSegmentWerkerContext(unreadJobs + newMsgJobs);
      },
      (_err) => {
        // console.log(err)
      }
    );

    return () => unsub();
  }, [profile?.UserId]);

  const invitedFeedJobs: WerkerJobData[] = useMemo(() => {
    return invitedFeedRaw.map((f) => {
      const startIso = f.startDateTimeRaw || "";
      const finishIso = f.finishDateTimeRaw || "";

      // Best-effort to Date
      const startMs = toMillis(startIso);
      const finishMs = toMillis(finishIso);

      const safeStart = startMs != null ? new Date(startMs) : null;
      const safeFinish = finishMs != null ? new Date(finishMs) : null;

      return {
        JobDocId: f.jobId,
        MyJobDocId: f.werkerJobDocId || "",
        ShiftIdentifier: f.shiftIdentifier || "",
        SchedUid: f.schedUid || "",

        DateDay: safeStart ? format(safeStart, "M/d/yy") : "",
        EndDay: safeFinish ? format(safeFinish, "M/d/yy") : "",
        StartDateTime: safeStart ? format(safeStart, "h:mma") : "",
        FinishDateTime: safeFinish ? format(safeFinish, "h:mma") : "",
        StartDateTimeRaw:
          typeof startIso === "string" ? startIso : startMs != null ? new Date(startMs).toISOString() : "",
        FinishDateTimeRaw:
          typeof finishIso === "string" ? finishIso : finishMs != null ? new Date(finishMs).toISOString() : "",

        Company: f.company || "",
        Location: f.location || "",
        LocationCoordinates: f.locationCoordinates || "",
        Pay: f.pay || "",
        ShiftNotes: f.shiftNotes || "",

        NumJobSlots: 0,
        JobStatusSched: "",
        JobStatusWerker: WORKFLOW_WERKER_TYPES.Invited,
        IsPaidJob: false,
        PaidWerker: false,
        JobCancelledByScheduler: f.status === "cancelled",
        CreatedAt: typeof f.createdAt === "number" ? f.createdAt : 0,
        UpdatedAt: typeof f.updatedAt === "number" ? f.updatedAt : 0,
        IsRead: f.isRead === true ? true : false,
        IsNewMessage: f.isNewMessage === true ? true : false,
        SchedFirstName: f.schedFirstName || "",
        SchedLastName: f.schedLastName || "",
        SchedProfilePic: f.schedProfilePicUrl || "",
        ScheduledCount: 0,

        // This is what your UI uses to ghost in-place
        __feedStatus: f.status, // "available" | "expired" | "cancelled"
      } as any;
    });
  }, [invitedFeedRaw]);

  // -----------------------------
  // Legacy listener: Scheduled + Past only
  // -----------------------------
  useEffect(() => {
    const uid = profile?.UserId;
    if (!uid) return;

    const werkerJobsListQuery = query(
      collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
      where("docStamp.userInfoTo.uid", "==", uid),
      where("jobInfo.status.jobStatusWerker", "in", [WORKFLOW_WERKER_TYPES.Scheduled, WORKFLOW_WERKER_TYPES.Past]),
      orderBy("jobInfo.core.dateTimeStart", "desc")
    );

    const unsubscribe = onSnapshot(
      werkerJobsListQuery,
      (querySnapshot) => {
        setDebugLegacySnapCount((p) => p + 1);

        const werkerJobDocs: WerkerJobData[] = [];

        let badgeCountWerkerScheduledJobs = 0;
        let badgeCountWerkerScheduledJobsNewMessages = 0;
        let badgeCountWerkerPastJobsNewMessages = 0;

        let tempScheduled = 0;
        let tempPast = 0;

        if (!querySnapshot.empty) {
          querySnapshot.forEach((docSnap) => {
            const jobDoc: any = docSnap.data();

            const werkerJobObject: WerkerJobData = {
              JobDocId: jobDoc.jobInfo.core.uniqueJobId,
              MyJobDocId: docSnap.id,
              ShiftIdentifier: jobDoc.jobInfo.core.shiftIdentifier || "",
              SchedUid: jobDoc.docStamp.userInfoFrom.uid,

              DateDay: format(new Date(jobDoc.jobInfo.core.dateTimeStart), "M/d/yy") || "",
              EndDay: format(new Date(jobDoc.jobInfo.core.dateTimeFinish), "M/d/yy") || "",
              StartDateTime: format(new Date(jobDoc.jobInfo.core.dateTimeStart), "h:mma") || "",
              FinishDateTime: format(new Date(jobDoc.jobInfo.core.dateTimeFinish), "h:mma") || "",
              StartDateTimeRaw: jobDoc.jobInfo.core.dateTimeStart,
              FinishDateTimeRaw: jobDoc.jobInfo.core.dateTimeFinish,

              Company: jobDoc.jobInfo.core.company,
              Location: jobDoc.jobInfo.core.location,
              LocationCoordinates: jobDoc.jobInfo.core.locationCoordinates,
              Pay: jobDoc.jobInfo.core.pay,
              ShiftNotes: jobDoc.jobInfo.core.shiftNotes,

              NumJobSlots: jobDoc.jobInfo.core.numJobSlots,
              JobStatusSched: jobDoc.jobInfo.status.jobStatusSched,
              JobStatusWerker: jobDoc.jobInfo.status.jobStatusWerker,
              IsPaidJob: jobDoc.jobInfo.status.isPaidJob,
              PaidWerker: jobDoc.jobInfo.status.isPaidWerker,
              JobCancelledByScheduler: jobDoc.jobInfo.status.jobCancelledByScheduler,

              CreatedAt: jobDoc.docStamp.timestamp.createdAt,
              UpdatedAt: jobDoc.docStamp.timestamp.updatedAt,
              IsRead: jobDoc.jobInfo.status.isRead,
              IsNewMessage: jobDoc.jobInfo.status.isNewMessage,

              SchedFirstName: jobDoc.docStamp.userInfoFrom.firstName,
              SchedLastName: jobDoc.docStamp.userInfoFrom.lastName,
              SchedProfilePic: jobDoc.docStamp.userInfoFrom.profilePicURL,
              ScheduledCount: jobDoc.jobInfo.status.scheduledCount,
            };
            werkerJobDocs.push(werkerJobObject);

            if (werkerJobObject.JobStatusWerker === WORKFLOW_WERKER_TYPES.Scheduled) {
              tempScheduled += 1;

              if (werkerJobObject.IsRead === false) badgeCountWerkerScheduledJobs += 1;
              if (werkerJobObject.IsNewMessage === true) badgeCountWerkerScheduledJobsNewMessages += 1;
            }
            if (werkerJobObject.JobStatusWerker === WORKFLOW_WERKER_TYPES.Past) {
              tempPast += 1;

              if (werkerJobObject.IsNewMessage === true) badgeCountWerkerPastJobsNewMessages += 1;
            }
          });
        }
        setWerkerJob(werkerJobDocs);
        setScheduledJobsCount(tempScheduled);
        setPastJobsCount(tempPast);

        setBadgeCountScheduledSegmentWerkerContext(
          badgeCountWerkerScheduledJobs + badgeCountWerkerScheduledJobsNewMessages
        );
        setBadgeCountPastSegmentWerkerContext(badgeCountWerkerPastJobsNewMessages);
      },
      (_err) => {
        // console.log(err)
      }
    );
    return () => unsubscribe();
  }, [profile?.UserId]);

  // Toggle badge count for header (sum of all segments)
  const badgeCountHeaderToggleWerkerContext = useMemo(() => {
    return (
      badgeCountInvitedSegmentWerkerContext +
      badgeCountScheduledSegmentWerkerContext +
      badgeCountPastSegmentWerkerContext
    );
  }, [
    badgeCountInvitedSegmentWerkerContext,
    badgeCountScheduledSegmentWerkerContext,
    badgeCountPastSegmentWerkerContext,
  ]);

  const werkerContextValue = useMemo(() => ({
    werkerJob,
    invitedFeedJobs,
    availableJobsCount,
    scheduledJobsCount,
    pastJobsCount,
    badgeCountInvitedSegmentWerkerContext,
    badgeCountScheduledSegmentWerkerContext,
    badgeCountPastSegmentWerkerContext,
    badgeCountHeaderToggleWerkerContext,
    debugFeedSnapCount,
    debugLegacySnapCount,
  }), [
    werkerJob,
    invitedFeedJobs,
    availableJobsCount,
    scheduledJobsCount,
    pastJobsCount,
    badgeCountInvitedSegmentWerkerContext,
    badgeCountScheduledSegmentWerkerContext,
    badgeCountPastSegmentWerkerContext,
    badgeCountHeaderToggleWerkerContext,
    debugFeedSnapCount,
    debugLegacySnapCount,
  ]);

  return (
    <WerkerJobContext.Provider value={werkerContextValue}>
      {children}
    </WerkerJobContext.Provider>
  );
};
